// useAlly — owns the Ally chat state machine.
// Inputs: CAG context (string from @content/ally-context).
// Outputs: messages, busy flag, send(question), reset().
//
// Behaviour:
//   1. POST to /api/ally with { message, context }.
//   2. On success, append the model's reply.
//   3. On failure (network, 4xx, 5xx), use the keyword fallback.
//   4. Aborts any in-flight request on send/unmount.

import { useCallback, useEffect, useRef, useState } from 'react';
import { allyFallbacks, allyGenericFallback } from '@content/ally-fallbacks';

export type AllyMessageRole = 'user' | 'assistant';

export interface AllyMessage {
  role: AllyMessageRole;
  text: string;
}

interface AllyApiResponse {
  reply?: string;
  error?: string;
  model?: string;
  remaining?: number;
  retryAfter?: number;
}

export interface UseAllyOptions {
  context: string;
  greeting: string;
  endpoint?: string;
}

export interface UseAllyResult {
  messages: AllyMessage[];
  busy: boolean;
  send: (question: string) => Promise<void>;
  reset: () => void;
}

function fallbackAnswer(question: string): string {
  for (const { pattern, answer } of allyFallbacks) {
    if (pattern.test(question)) return answer;
  }
  return allyGenericFallback;
}

export function useAlly({ context, greeting, endpoint = '/api/ally' }: UseAllyOptions): UseAllyResult {
  const greetingMessage: AllyMessage = { role: 'assistant', text: greeting };
  const [messages, setMessages] = useState<AllyMessage[]>([greetingMessage]);
  const [busy, setBusy] = useState(false);
  const ctrlRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      ctrlRef.current?.abort();
    };
  }, []);

  const send = useCallback(
    async (rawQuestion: string): Promise<void> => {
      const question = rawQuestion.trim();
      if (!question || busy) return;

      // Abort any prior in-flight request before starting a new one.
      ctrlRef.current?.abort();
      const ctrl = new AbortController();
      ctrlRef.current = ctrl;

      setMessages((prev) => [...prev, { role: 'user', text: question }]);
      setBusy(true);

      let reply = '';
      try {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ message: question, context }),
          signal: ctrl.signal,
        });
        if (res.ok) {
          const data: AllyApiResponse = await res.json();
          reply = (data.reply ?? '').trim();
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          // Component is unmounting or a newer send superseded us. Drop quietly.
          return;
        }
      }

      if (!reply) reply = fallbackAnswer(question);

      setMessages((prev) => [...prev, { role: 'assistant', text: reply }]);
      setBusy(false);
    },
    [busy, context, endpoint]
  );

  const reset = useCallback((): void => {
    ctrlRef.current?.abort();
    setMessages([greetingMessage]);
    setBusy(false);
    // greetingMessage is recomputed each render; reset reads the latest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [greeting]);

  return { messages, busy, send, reset };
}
